import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { usePrevious } from '../../utils/custom-hooks';
import { connect } from 'react-redux';
import { isDesktop } from 'react-device-detect';
import { dashboardBgColor } from './ui-kit/utils';
import { isMobileOnly } from 'react-device-detect';
import { operations as dashboardOperations, selectors as dashboardSelectors } from '../../state/modules/dashboards';
import { operations as archetypeOperations } from '../../state/modules/widget-archetypes';
import { selectors as widgetArchetypeSelectors } from '../../state/modules/widget-archetypes';
import { selectors as groupSelectors } from '../../state/modules/groups';
import { operations as activityTypeOperations } from '../../state/modules/activity-types';
import { getCurrentUser } from '../../state/modules/current-user/operations';
import { checkMemory } from '../../utils/cable/monitoring';
import DashboardAPIManager from './DashboardAPIManager';
import DashboardToolbar from './dashboard-toolbar/DashboardToolbar';
import WidgetStore from './WidgetStore';
import NewDashboardModal from './NewDashboardModal';
import UnsavedChangesWarningModal from './UnsavedChangesWarningModal';
import ReturnToNormalModeModal from './ReturnToNormalModeModal';
import DashboardSettingsDialog from './DashboardSettingsDialog';
import { operations as incidentOperations } from '../../state/modules/incidents';
import { operations as responseOperations } from '../../state/modules/incident-responses';
import {
  stationToIncidentsMap,
  anyActiveIncidents,
  selectDisplayableIncidents,
  selectCurrentDisplayedIncident,
  displayedIncidentId
} from '../../state/modules/incidents/selectors';
import { redirectTo } from '../../utils/redirect';
import ConnectionStatus from '../general/ConnectionStatus';
import DashboardLoadingStatus from './DashboardLoadingStatus';
import Loader from '../general/Loader';
import DashboardPageResponsive from './DashboardPageResponsive';
import { PortalDestination } from '../ui-kit';
import FontSizeMultiplierContext from './FontSizeMultiplierContext';
import AccessRightsContext from '../contexts/AccessRightsContext';

const isSingleWidgetMode = dashboard => dashboard.tag === 'region_map';

const calculateShouldDisplayToolbar = ({
  dashboard,
  dashboards,
  editRights,
  stationMonitor,
  anyActiveIncidents,
  incidentMode
}) => {
  if (isSingleWidgetMode(dashboard)) return false;
  if (incidentMode && anyActiveIncidents) return false;
  if (stationMonitor) return false;

  if (dashboard.always_show_toolbar_for_normal_users) return true;

  // if you have no edit rights, and there is only one dashboard, hide the
  // toolbar. This is to simplify the view for non expert users
  if (!editRights && dashboards.length === 1) return false;
  // if there are two dashboards, and one is the default dashboard, and the
  // other is the station monitor dashboard, hide the toolbar.
  if (!editRights && dashboards.length === 2) {
    const defaultDashboard = dashboards.find(d => d.default);
    const defaultStationMonitorDashboard = dashboards.find(d => d.default_for_station_monitor);

    // If both defaults have values, and they don't have the same ID,
    // hide the toolbar
    if (
      defaultDashboard &&
      defaultStationMonitorDashboard &&
      defaultDashboard.id !== defaultStationMonitorDashboard.id
    ) {
      return false;
    }
  }

  return true;
};

const Dashboard = props => {
  const { dashboard, accessRights } = props;
  const dashboardId = dashboard?.id;
  const {
    fetchActivityTypes,
    fetchCurrentDashboard,
    fetchAllDashboards,
    saveDashboardToBackend,
    editRights,
    setDashboardMode
  } = props;

  // edit mode is stored in redux
  const setEditMode = props.setDashboardEditMode;
  const editMode = props.dashboardEditMode;
  const previousAnyActiveIncidents = usePrevious(props.anyActiveIncidents);

  const [showWidgetStore, setShowWidgetStore] = useState(false);
  const [showNewDashboardModal, setShowNewDashboardModal] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showReturnToNormalMode, setShowReturnToNormalMode] = useState(false);
  const [showDashboardSettings, setShowDashboardSettings] = useState(false);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);

  const fontSizeMultiplier = isMobileOnly ? 100 : dashboard.font_size_multiplier;

  // State change functions
  const markAsChanged = useCallback(() => setUnsavedChanges(true), []);

  const enterEditMode = useCallback(() => setEditMode(true), [setEditMode]);

  const exitEditMode = useCallback(
    force => {
      if (unsavedChanges && !force) {
        setShowUnsavedChangesWarning(true);
      } else {
        setEditMode(false);
      }
    },
    [unsavedChanges, setEditMode, setShowUnsavedChangesWarning]
  );

  // Whenever the 'New dashboard' modal is opened, load the templates
  useEffect(() => {
    if (showNewDashboardModal) fetchAllDashboards({ forceFetch: true, params: { templates: true } });
  }, [showNewDashboardModal, fetchAllDashboards]);

  const hideUnsavedChangesWarning = useCallback(() => setShowUnsavedChangesWarning(false), []);

  const returnToNormalMode = useCallback(() => {
    setUnsavedChanges(false);
    setShowWidgetStore(false);
    setShowReturnToNormalMode(false);
    setEditMode(false);
    saveDashboardToBackend(dashboardId);
  }, [dashboardId, saveDashboardToBackend, setEditMode, setShowReturnToNormalMode, setShowWidgetStore]);

  // Callbacks (used by childen component)
  const createDashboard = useCallback(
    (type, templateId) => {
      const groupId = dashboard.group_id;
      props.createDashboard(groupId, type, templateId).then(dashboard => {
        const url = `/groups/${groupId}/dashboards/${dashboard.id}`;
        redirectTo(url);
      });
    },
    [dashboardId]
  );

  const saveDashboard = useCallback(() => {
    if (!dashboardId) return;

    // There are some changes that can be done outside of edit mode.
    // For example: toggling the showHeader option.
    if (!editMode) {
      setUnsavedChanges(false);
      editRights && saveDashboardToBackend(dashboardId);
      return;
    }

    if (props.activePage.page_type === 'normal') {
      setUnsavedChanges(false);
      setShowWidgetStore(false);
      // setEditMode(false);
      // check for editRights. This is mainly done for the autoscroll
      // functionality. The autoscroll toggle is always visible, but for those
      // who have edit rights, it will also persist to the backend.
      editRights && saveDashboardToBackend(dashboardId);
    } else {
      setShowReturnToNormalMode(true);
    }
  }, [dashboardId, props.activePage?.id]);

  const deleteDashboard = useCallback(() => {
    const groupId = dashboard.group_id;
    props.deleteDashboardFromBackend(dashboardId).then(() => {
      const url = `/groups/${groupId}/dashboard`;
      redirectTo(url);
    });
  }, [dashboardId]);

  const addWidget = useCallback(
    archetypeId => {
      setUnsavedChanges(true);

      // as default title for a widget, use the name of the archetype
      const title = props.widgetArchetypes[archetypeId].title;

      const widget = {
        position_x: 0,
        position_y: 0,
        height: 10,
        width: props.columns ? props.columns / 2 : 3,
        archetype_id: archetypeId,
        title
      };

      props.addWidget(widget, props.activePageIndex);
    },
    [props.activePageIndex, props.widgetArchetypes]
  );

  const updateTitle = useCallback(title => {
    setUnsavedChanges(true);
    props.updateCurrentDashboardFields({ title });
  }, []);

  const updateWidgetTitle = useCallback((id, title) => {
    setUnsavedChanges(true);
    props.updateWidgetFields(id, { title });
  }, []);

  const updateWidgetHeight = useCallback((id, height) => {
    props.updateWidgetFields(id, { height });
  }, []);

  const updateWidgetSettings = useCallback(
    (id, settings) => {
      setUnsavedChanges(true);
      props.updateWidgetSettings(id, settings);
    },
    [props.updateWidgetSettings]
  );

  const displayDashboardSettings = useCallback(() => {
    fetchActivityTypes();
    setShowDashboardSettings(true);
  }, [fetchActivityTypes, setShowDashboardSettings]);

  const hideDashboardSettings = useCallback(() => setShowDashboardSettings(false), []);

  const fetchDashboard = useCallback(() => {
    fetchCurrentDashboard(props.id);
    new DashboardAPIManager().registerForDashboardUpdates({ dashboardId: props.id });
  }, [props.id, fetchCurrentDashboard]);

  // Hooks

  useEffect(() => {
    const dashboardAPIManager = new DashboardAPIManager();

    setDashboardMode(true);

    // IS THIS NECESSARY? This always gets the groups and subgroups, even if
    // there are no widgets on the dashboard which need this. TODO all widgets
    // need to load the data that they need, and not rely on this call
    dashboardAPIManager.fetchGroupAndSubgroups();
    dashboardAPIManager.registerForIncidentUpdates();
    fetchDashboard();

    if (isDesktop) {
      const body = document.querySelectorAll('body')[0];
      body.style.background = dashboardBgColor;
    }

    // Schedule a periodic memory consumption check (every 5 minutes). If the memory
    // consumption exceeds a certain threshold, the page will be reloaded.
    // For RPi devices, the memory heap is of 2GB, and we are aiming to cap it at
    // 512 MB after behavioral analysis (25%). We suspect incidents and WS updates
    // cause a memory leak. This is a temporary solution.
    const interval = setInterval(() => checkMemory(0.25), 300_000);

    return () => clearInterval(interval);
  }, []);

  useEffect(
    () => {
      if (props.groupId) props.fetchAllDashboards({ forceFetch: true, params: { group_id: props.groupId } });
      props.fetchWidgetArchetypes();
      props.getCurrentUser(); // for hidden availability requirement ids
    },
    // don't use props.dashboard. This will refesh dashboards after every
    // change, including to widget settings
    [dashboardId]
  );

  useEffect(() => {
    const { anyActiveIncidents, dashboard, activateIncidentMode, activateNormalMode } = props;

    if (anyActiveIncidents && dashboard.incident_mode_enabled) {
      activateIncidentMode();
    } else if (!editMode) {
      activateNormalMode();
    }

    // Clean up old incidents to free up memory
    if (!anyActiveIncidents && previousAnyActiveIncidents) {
      props.purgeIncidents();
      props.purgeIncidentResponses();
    }
  }, [props.anyActiveIncidents, dashboard]);

  useEffect(() => {
    if (!editMode) {
      if (unsavedChanges) fetchDashboard();

      setUnsavedChanges(false);
      setShowWidgetStore(false);
      setShowUnsavedChangesWarning(false);
    }
  }, [editMode]);

  useEffect(() => {
    const handleBeforeUnload = event => {
      if (unsavedChanges) event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [unsavedChanges]);

  if (props.dashboardsLoading && !dashboard) return <Loader />;
  const singleWidgetMode = isSingleWidgetMode(dashboard);
  const incidentMode = props.activePage.page_type === 'incident' && !singleWidgetMode;

  const shouldDisplayToolbar = calculateShouldDisplayToolbar({
    dashboard,
    dashboards: props.dashboards,
    editRights: props.editRights,
    stationMonitor: props.stationMonitor,
    anyActiveIncidents: props.anyActiveIncidents,
    incidentMode
  });

  return (
    <Suspense fallback={<Loader />}>
      <AccessRightsContext.Provider value={accessRights || {}}>
        <PortalDestination />
        {showUnsavedChangesWarning && (
          <UnsavedChangesWarningModal
            hideUnsavedChangesWarning={hideUnsavedChangesWarning}
            exitEditMode={exitEditMode}
            saveChanges={returnToNormalMode}
          />
        )}
        {showReturnToNormalMode && <ReturnToNormalModeModal returnToNormalMode={returnToNormalMode} />}
        <DashboardSettingsDialog
          show={showDashboardSettings}
          hide={hideDashboardSettings}
          dashboard={dashboard}
          updateDashboardFields={props.updateCurrentDashboardFields}
          saveDashboard={saveDashboard}
          activityTypes={props.activityTypes}
          groupDashboardEditRights={props.groupDashboardEditRights}
          groupsTree={props.groupsTree}
        />
        {shouldDisplayToolbar && (
          <DashboardToolbar
            incidentMode={incidentMode}
            editMode={editMode}
            activateNormalMode={props.activateNormalMode}
            activateIncidentMode={props.activateIncidentMode}
            setDashboardDatetime={props.setDashboardDatetime}
            dashboardDatetime={props.dashboardDatetime}
            unlockDashboardDatetime={props.unlockDashboardDatetime}
            dashboardTitle={dashboard.title}
            currentDashboard={dashboard}
            updateTitle={updateTitle}
            accessibleDashboards={props.dashboards}
            groupDashboardEditRights={props.groupDashboardEditRights}
            editRights={props.editRights}
            dashboardIsDefault={dashboard.default}
            showDashboardSettings={displayDashboardSettings}
            incidentModeEnabled={dashboard.incident_mode_enabled}
            enterEditMode={enterEditMode}
            exitEditMode={exitEditMode}
            showWidgetStore={setShowWidgetStore}
            showNewDashboardModal={setShowNewDashboardModal}
            saveDashboard={saveDashboard}
            deleteDashboard={deleteDashboard}
            dashboardIsPersonal={dashboard.personal}
            unsavedChanges={unsavedChanges}
            updateDashboardFields={props.updateCurrentDashboardFields}
          />
        )}
        {editMode && <div style={{ height: '2.7rem' }} />}
        {showWidgetStore && (
          <WidgetStore
            widgetArchetypes={props.widgetArchetypes}
            showWidgetStore={setShowWidgetStore}
            addWidget={addWidget}
          />
        )}
        {showNewDashboardModal && (
          <NewDashboardModal
            groupDashboardEditRights={props.groupDashboardEditRights}
            createDashboard={createDashboard}
            setShowNewDashboardModal={setShowNewDashboardModal}
            templates={props.dashboardTemplates}
          />
        )}
        <FontSizeMultiplierContext.Provider value={fontSizeMultiplier}>
          <DashboardPageResponsive
            editMode={editMode}
            activePage={props.activePage}
            saveDashboard={saveDashboard}
            updateWidgets={props.updateWidgets}
            updateWidgetSettings={updateWidgetSettings}
            activePageIndex={props.activePageIndex}
            removeWidget={props.removeWidget}
            activeIncidents={props.activeIncidents}
            setDisplayedIncidentId={props.setDisplayedIncidentId}
            widgetArchetypes={props.widgetArchetypes}
            activePageWidgetCoordinates={props.activePageWidgetCoordinates}
            dashboard={dashboard}
            singleWidgetMode={singleWidgetMode}
            displayableIncidents={props.displayableIncidents}
            displayNextIncident={props.displayNextIncident}
            currentDisplayedIncident={props.currentDisplayedIncident}
            markAsChanged={markAsChanged}
            updateWidgetTitle={updateWidgetTitle}
            updateWidgetHeight={updateWidgetHeight}
            columns={dashboard.columns}
            lockIncident={props.lockIncident}
            incidentLocked={props.incidentLocked}
            currentDisplayedIncidentId={props.currentDisplayedIncidentId}
          />
        </FontSizeMultiplierContext.Provider>
        <DashboardLoadingStatus />
        <ConnectionStatus />
      </AccessRightsContext.Provider>
    </Suspense>
  );
};

Dashboard.whyDidYouRender = true;

const mapStateToProps = state => ({
  dashboard: dashboardSelectors.selectCurrentDashboard(state),
  dashboards: dashboardSelectors.selectGroupDashboards(state),
  dashboardTemplates: dashboardSelectors.selectTemplates(state),
  dashboardsLoaded: dashboardSelectors.initialLoadComplete(state),
  dashboardDatetime: state.dashboards.dashboardDatetime,
  dashboardEditMode: state.dashboards.editMode,
  activePageIndex: state.dashboards.activePageIndex,
  activePage: dashboardSelectors.selectActivePage(state),
  activePageWidgetCoordinates: dashboardSelectors.activePageWidgetCoordinates(state),
  widgetArchetypes: widgetArchetypeSelectors.byId(state),
  widgetArchetypesLoaded: widgetArchetypeSelectors.initialLoadComplete(state),
  activityTypes: state.activityTypes.byId,
  activeIncidents: stationToIncidentsMap(state),
  groupsTree: groupSelectors.tree(state),
  anyActiveIncidents: anyActiveIncidents(state),
  displayableIncidents: selectDisplayableIncidents(state),
  currentDisplayedIncident: selectCurrentDisplayedIncident(state),
  currentDisplayedIncidentId: displayedIncidentId(state),
  incidentLocked: state.dashboards.locked
});

const mapDispatchToProps = {
  fetchCurrentDashboard: dashboardOperations.fetchCurrentDashboard,
  fetchAllDashboards: dashboardOperations.fetch,
  updateWidgets: dashboardOperations.updateWidgets,
  updateWidgetSettings: dashboardOperations.updateWidgetSettings,
  createDashboard: dashboardOperations.createDashboard,
  saveDashboardToBackend: dashboardOperations.storeInBackend,
  deleteDashboardFromBackend: dashboardOperations.deleteFromBackend,
  updateCurrentDashboardFields: dashboardOperations.updateCurrentDashboardFields,
  updateWidgetFields: dashboardOperations.updateWidget,
  removeWidget: dashboardOperations.removeWidget,
  addWidget: dashboardOperations.addWidget,
  setDashboardDatetime: dashboardOperations.setDashboardDatetime,
  setDashboardMode: dashboardOperations.setDashboardMode,
  unlockDashboardDatetime: dashboardOperations.unlockDashboardDatetime,
  fetchWidgetArchetypes: archetypeOperations.fetch,
  getCurrentUser: getCurrentUser,
  activateIncidentMode: dashboardOperations.activateIncidentMode,
  activateNormalMode: dashboardOperations.activateNormalMode,
  setDisplayedIncidentId: dashboardOperations.setDisplayedIncidentId,
  displayNextIncident: dashboardOperations.displayNextIncident,
  fetchActivityTypes: activityTypeOperations.fetch,
  setDashboardEditMode: dashboardOperations.setEditMode,
  lockIncident: dashboardOperations.lockIncident,
  purgeIncidents: incidentOperations.purge,
  purgeIncidentResponses: responseOperations.purge
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
